/**
 * Case replay — parse a saved case recording (JSON or CSV export from the
 * CaseRecorder) and provide helpers to reconstruct the monitor state at any
 * point on the timeline.
 *
 * A case has:
 *   samples : [{ t(ms), values:{ HR, SpO2, ... } }]   — vitals every ~5 s
 *   events  : [{ t(ms), kind, label }]                — scenarios/drugs/shocks
 *   keyframes : events + auto-detected major vitals changes (for ⏮/⏭ stepping)
 */
import { SCENARIOS } from './scenarioEngine';

export const REPLAY_VITAL_KEYS = [
  'HR', 'SpO2', 'NIBP_Sys', 'NIBP_Dia', 'ABP_Sys', 'ABP_Dia', 'EtCO2', 'RR', 'Temp', 'CVP',
];

// Scenario/shock display name → ECG rhythm, so replay can show the right
// waveform morphology (VF, VT, etc.) inferred from the logged scenario events.
const NAME_TO_RHYTHM = (() => {
  const m = {};
  for (const s of Object.values(SCENARIOS)) if (s.name && s.rhythm) m[s.name] = s.rhythm;
  return m;
})();

const mmssToMs = (str) => {
  const parts = String(str).split(':').map(Number);
  const mm = parts[0] || 0, ss = parts[1] || 0;
  return (mm * 60 + ss) * 1000;
};

// ── Parsing ──────────────────────────────────────────────────────────────────
export function parseCaseFile(text) {
  const trimmed = String(text).trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) return normalizeJSON(JSON.parse(trimmed));
  return normalizeCSV(trimmed);
}

function pickVitals(s) {
  const v = {};
  for (const k of REPLAY_VITAL_KEYS) { const n = Number(s[k]); if (Number.isFinite(n)) v[k] = n; }
  return v;
}

function normalizeJSON(obj) {
  const samples = (obj.samples || []).map((s) => ({
    t: Number(s.t) || 0,
    values: pickVitals(s),
    capno: s.capno || null,       // capnograph morphology
    split: !!s.split,             // ventilator screen shown
    intubated: !!s.intubated,     // airway status (drives vent STANDBY vs active)
    vent: s.vent || null,         // ventilator settings snapshot
  }));
  const events = (obj.events || []).map((e) => ({ t: Number(e.t) || 0, kind: e.kind || 'event', label: e.label || '' }));
  return finalize(samples, events, obj.durationMs);
}

function parseCSVLine(line) {
  const out = []; let cur = '', q = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (q) { if (c === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else q = false; } else cur += c; }
    else if (c === ',') { out.push(cur); cur = ''; }
    else if (c === '"') { q = true; }
    else cur += c;
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

function normalizeCSV(text) {
  const lines = text.split(/\r?\n/);
  const samples = [], events = [];
  let mode = null, header = null;
  for (const line of lines) {
    if (/^VITALS/i.test(line)) { mode = 'vitals'; header = null; continue; }
    if (/^EVENTS/i.test(line)) { mode = 'events'; header = null; continue; }
    if (!line.trim() || !mode) continue;
    const cells = parseCSVLine(line);
    if (mode === 'vitals') {
      if (!header) { header = cells; continue; }
      const values = {};
      header.slice(1).forEach((k, i) => { const n = Number(cells[i + 1]); if (Number.isFinite(n)) values[k] = n; });
      samples.push({ t: mmssToMs(cells[0]), values });
    } else {
      if (!header) { header = cells; continue; }
      const hasClock = /clock/i.test(header[1] || '');   // new export: Elapsed,Clock,Type,Detail
      events.push({
        t: mmssToMs(cells[0]),
        kind: (hasClock ? cells[2] : cells[1]) || 'event',
        label: (hasClock ? cells[3] : cells[2]) || '',
      });
    }
  }
  return finalize(samples, events);
}

function finalize(samples, events, durationHint) {
  samples.sort((a, b) => a.t - b.t);
  events.sort((a, b) => a.t - b.t);
  const lastT = Math.max(
    samples.length ? samples[samples.length - 1].t : 0,
    events.length ? events[events.length - 1].t : 0,
    0,
  );
  const durationMs = Math.max(Number(durationHint) || 0, lastT);
  const vitalKeys = REPLAY_VITAL_KEYS.filter((k) => samples.some((s) => k in s.values));
  const hasVent = samples.some((s) => s.vent);
  return { durationMs, samples, events, vitalKeys, hasVent, keyframes: buildKeyframes(samples, events) };
}

/** Ventilator/capnograph state at time t — the most recent sample at/before t. */
export function ventAt(caseData, t) {
  let cur = null;
  for (const s of caseData.samples) { if (s.t > t) break; cur = s; }
  if (!cur) return { capno: null, split: false, intubated: false, vent: null, aid: null, media: null };
  return { capno: cur.capno || null, split: !!cur.split, intubated: !!cur.intubated, vent: cur.vent || null, aid: cur.aid ?? null, media: cur.media ?? null };
}

// ── Keyframes (events + detected deterioration) ──────────────────────────────
const CHANGE_THRESH = { HR: 16, SpO2: 4, NIBP_Sys: 15, ABP_Sys: 15, EtCO2: 8, RR: 6, CVP: 4 };

function buildKeyframes(samples, events) {
  const kf = new Map();
  events.forEach((e) => kf.set(e.t, { t: e.t, kind: e.kind, label: e.label }));
  for (let i = 1; i < samples.length; i++) {
    const a = samples[i - 1].values, b = samples[i].values;
    const changes = [];
    for (const k of Object.keys(CHANGE_THRESH)) {
      if (a[k] != null && b[k] != null && Math.abs(b[k] - a[k]) >= CHANGE_THRESH[k]) {
        changes.push((b[k] > a[k] ? '↑' : '↓') + k.replace('_Sys', ''));
      }
    }
    if (changes.length) {
      const t = samples[i].t;
      if (!kf.has(t)) kf.set(t, { t, kind: 'change', label: changes.join(' ') });
    }
  }
  return [...kf.values()].sort((a, b) => a.t - b.t);
}

// ── State at time t ──────────────────────────────────────────────────────────
export function interpolateAt(caseData, t) {
  const { samples } = caseData;
  if (!samples.length) return {};
  if (t <= samples[0].t) return { ...samples[0].values };
  if (t >= samples[samples.length - 1].t) return { ...samples[samples.length - 1].values };
  let i = 0;
  while (i < samples.length - 1 && samples[i + 1].t <= t) i++;
  const a = samples[i], b = samples[i + 1];
  const f = (t - a.t) / Math.max(1, b.t - a.t);
  const out = {};
  REPLAY_VITAL_KEYS.forEach((k) => {
    const va = a.values[k], vb = b.values[k];
    if (va != null && vb != null) out[k] = va + (vb - va) * f;
    else if (va != null) out[k] = va;
    else if (vb != null) out[k] = vb;
  });
  return out;
}

const cleanLabel = (l) => String(l).replace(/\s+[—-]\s+.*$/, '').trim(); // strip "— no ROSC" etc.

/** ECG rhythm at time t, inferred from the most recent scenario/shock event. */
export function rhythmAt(caseData, t) {
  let rhythm = 'normal';
  for (const e of caseData.events) {
    if (e.t > t) break;
    if (e.kind === 'scenario' || e.kind === 'shock') {
      if (/defib|cardiovers/i.test(e.label)) { rhythm = 'normal'; continue; }
      const r = NAME_TO_RHYTHM[cleanLabel(e.label)];
      rhythm = r || (e.kind === 'scenario' ? 'normal' : rhythm);
    }
  }
  return rhythm;
}

/** The most recent event at//before time t (for the "now playing" label). */
export function eventAt(caseData, t) {
  let cur = null;
  for (const e of caseData.events) { if (e.t > t) break; cur = e; }
  return cur;
}

export const mmss = (ms) => {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
